import { useMemo } from "react";

import useTypedContext from "hooks/useTypedContext";
import useFlowsIntegration from "shared/Flows/useFlowsIntegration";
import { AccountContext } from "views/AccountWrapper";
import { DataFlow, Rocket } from "components/icons/generated";
import { type IconComponent } from "types/Icon";

export type ProductSwitcherProductId = "spacelift" | "flows";

export type ProductSwitcherProduct = {
  id: ProductSwitcherProductId;
  title: string;
  description: string;
  icon: IconComponent;
  isCurrent: boolean;
  href?: string;
  onAction?: () => void;
  isDisabled?: boolean;
  tooltip?: string;
  isExternal?: boolean;
};

const FLOWS_NOT_LINKED_NON_ADMIN_TOOLTIP =
  "Flows organization is not linked. You need to have admin permissions to link it.";

const openFlowsInNewTab = (path: string) => {
  window.open(path, "_blank");
};

const buildFlowsProduct = (isLinked: boolean, isAdmin: boolean): ProductSwitcherProduct => {
  const base = {
    id: "flows",
    title: "Flows",
    description: "Automate your workflows",
    icon: DataFlow,
    isCurrent: false,
    isExternal: true,
  } as const;

  if (isLinked) {
    // /flows/login is a backend endpoint that 302s to Flows.
    return { ...base, onAction: () => openFlowsInNewTab("/flows/login") };
  }

  if (isAdmin) {
    return { ...base, onAction: () => openFlowsInNewTab("/flows/link/select") };
  }

  return { ...base, isDisabled: true, tooltip: FLOWS_NOT_LINKED_NON_ADMIN_TOOLTIP };
};

const useProductSwitcherProducts = () => {
  const { viewer } = useTypedContext(AccountContext);
  const { isLinked } = useFlowsIntegration();

  return useMemo(() => {
    const spacelift: ProductSwitcherProduct = {
      id: "spacelift",
      title: "Deploy",
      description: "Orchestrate your IaC",
      icon: Rocket,
      isCurrent: true,
      // Folding the logo into the switcher drops the logo→dashboard link, so the current
      // product row carries that redirect to the default view.
      href: "/",
    };

    return [spacelift, buildFlowsProduct(isLinked, viewer.admin)];
  }, [isLinked, viewer.admin]);
};

export default useProductSwitcherProducts;
